<script setup>
    import { onMounted, ref, nextTick, defineOptions, defineProps } from 'vue';
    import { onSnapshot, collection, query, where, getDocs, orderBy, startAfter, limit } from 'firebase/firestore';
    import { db } from '@/firebase';
    import { timeSimplified, localToUTC, utcToLocal } from '@/tools/TimeFunctions';
    import { isWithinRadius } from '@/tools/Geofence';
    import LoadingAnimation3 from '../loadingIcons/LoadingAnimation3.vue';
    import DatePicker from '../dropdowns/DatePicker.vue';

// Set up Google Maps (only once)
if (!window.googleMapsInitialized) {
    window.googleMapsInitialized = true;
    (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise((f,n)=>{a=m.createElement("script");e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=`https://maps.${c}apis.com/maps/api/js?`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once."):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
        key: process.env.VUE_APP_GOOGLE_MAPS_API_KEY || "AIzaSyAdnZ7SDSCwfssmTuoztLkglh9w06vbPg8",
        v: "weekly",
    });
}

    /* global google */
    defineOptions({
        name: 'UnitMap'
    });

    const props = defineProps({
        dataID: String,
        template: Object,
        timeFormat: {
            type: String,
            default: '24hour'
        }
    });

    //Set up variable references.
    const mapContainer = ref(null);
    const showPolyline = ref(false);
    const dateValue = ref([]);
    const control = ref(null);
    const loading = ref(false);
    const lastUpdate = ref('');

    //Set other global variables.
    let map;
    let marker;
    let bounds;
    let polyline;
    let unsubscribe;
    let infoWindow;
    let center = true;
    const batchSize = 1000; // DONT CHANGE
    const geofenceRadius = 0.01; // Kilometers

    /**
     * Initialize google maps
     */
    onMounted(async() => {
        // Wait for next tick and then check for map container
        await nextTick();
        
        // Wait for map container to be available (with retry mechanism for Firefox)
        let retryCount = 0;
        const maxRetries = 10;
        while (!mapContainer.value && retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 100));
            retryCount++;
        }
        
        if (!mapContainer.value) {
            console.error('UnitMap: Map container element is not available after retries');
            return;
        }
        
        try {
            // Ensure google.maps is available
            if (typeof google === 'undefined' || !google.maps) {
                throw new Error('Google Maps API not loaded');
            }

            // Request needed libraries.
            const { Map } = await google.maps.importLibrary("maps");
            await google.maps.importLibrary("marker");
            
            map = new Map(mapContainer.value, {
                center: new google.maps.LatLng(39.22696383208982, -98.91715880586187),
                zoom: 5,
                mapId: "Meshportal Map",
                mapTypeId: google.maps.MapTypeId.HYBRID,
                mapTypeControl: true,
                mapTypeControlOptions: {
                    style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
                    position: google.maps.ControlPosition.TOP_CENTER,
                },
                streetViewControl: true,
                fullscreenControl: true,
            });

            // Wait for map to be fully loaded before proceeding
            await new Promise((resolve) => {
                google.maps.event.addListenerOnce(map, 'idle', resolve);
            });
            
        } catch (error) {
            console.error('UnitMap: Error initializing map:', error);
            
            // Show a user-friendly error message instead of letting it bubble up
            if (mapContainer.value) {
                mapContainer.value.innerHTML = `
                    <div class="flex items-center justify-center h-full bg-mc-cloud dark:bg-mc-charcoal text-ink-muted dark:text-on-dark-muted">
                        <div class="text-center">
                            <p class="text-lg font-semibold mb-2 text-mc-ink dark:text-on-dark">Map Loading Error</p>
                            <p class="text-sm">Unable to load Google Maps. Please refresh the page or try again later.</p>
                        </div>
                    </div>
                `;
            }
            return;
        }

        // Initiate the info window for markers
        infoWindow = new google.maps.InfoWindow({
            content: "",
            disableAutoPan: true,
        });

        // Close the info window if clicked off on the map
        google.maps.event.addListener(map, "click", () => {
            if (infoWindow) {
                infoWindow.close();
            }
        });

        // If the user changed the zoom, stop centering
        google.maps.event.addListener(map, 'zoom_changed', () => {
            center = false;
        });

        // If the user changed the zoom, stop centering
        google.maps.event.addListener(map, 'dragstart', () => {
            center = false;
        });

        // Add a recenter button to the map
        const controlDiv = document.createElement('div');
        if (control.value) {
            controlDiv.appendChild(control.value);
            map.controls[google.maps.ControlPosition.TOP_LEFT].push(controlDiv);
        }

        bounds = new google.maps.LatLngBounds();

        grabLocation();
    });

    /**
     * Continually grabs the current coordinates of the unit.
     */
    const grabLocation = async () => {
        const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker");

        // Remove poliline if it exists
        if (polyline) {
            polyline.setMap(null);
            polyline = null;
        }
        showPolyline.value = false

        const greenMarker = new PinElement({
            background: '#00ff00',
            borderColor: '#00ff00',
            glyphColor: "#0AA900"
        })

        // Start a snapshot listener
        const subcollectionRef = collection(db, 'data', props.dataID, props.template.source);
        const q = query(subcollectionRef, orderBy('messageUTC', 'desc'), limit(1));
        unsubscribe = onSnapshot(q, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                const docData = change.doc.data();

                // Make sure coordinates exist
                if (docData[props.template.latitudeField]){

                    lastUpdate.value = docData.messageUTC;

                    const coordinate = {
                        lat: docData[props.template.latitudeField],
                        lng: docData[props.template.longitudeField],
                        alt: docData[props.template.altitudeField],
                    };

                    // Update the marker if it already exists
                    if (marker) {
                        marker.position = coordinate;
                    } 
                    
                    // Initiate the marker
                    else {
                        marker = new AdvancedMarkerElement({
                            position: coordinate,
                            map: map,
                        });

                        marker.addListener("click", () => {
                            if(coordinate.alt) {
                                infoWindow.setContent(`
                                    <span class="font-bold">Altitude: </span>${coordinate.alt}<br>
                                    <span class="font-bold">Latitude: </span>${coordinate.lat}<br>
                                    <span class="font-bold">Longitude: </span>${coordinate.lng}
                                `);
                            } else{
                                infoWindow.setContent(`
                                    <span class="font-bold">Latitude: </span>${coordinate.lat}<br>
                                    <span class="font-bold">Longitude: </span>${coordinate.lng}
                                `);
                            }
                            infoWindow.open(map, marker);
                        });
                    }

                    if (docData.messageUTC && timeSimplified(utcToLocal(docData.messageUTC, props.timeFormat)) === 'Current'){
                        marker.content = greenMarker.element;
                    } else {
                        marker.content = null;
                    }

                    // If there was no human interaction with the map, then continue to
                    // recenter over the marker.
                    if (center) {
                        recenterMap();
                    }
                }
            });
        });
    };

    /**
     * reset the view position over the marker.
     */
    const recenterMap = () => {
        if (map && marker && marker.position) {
            map.setCenter(marker.position);
            map.setZoom(18);
            center = true;
        }
    };

    /**
     * generates a polyline of historical location.
     */
        const generatePolyline = async() => {
        loading.value = true;

        // Remove poliline if it exists
        if (polyline) {
            showPolyline.value = false;
            polyline.setMap(null);
            polyline = null;
        }

        //Stop live location snapshot
        if(marker) {
            unsubscribe();
            marker.setMap(null);
            marker = null;
            bounds = new google.maps.LatLngBounds();
        }

        //Grab the utc start and end date.
        const utcStart = localToUTC(dateValue.value[0]);
        const utcEnd =  localToUTC(dateValue.value[1]);

        //set up initial variables
        let polylineCoords = [];
        let size = 0;
        let lastDoc = null;
        let previousCoord = {lat: 0, lng: 0};
        let q;

        //Querry historical locations from database.
        do {
            if (lastDoc) {
                q = query(
                    collection(db, 'data', props.dataID, 'location'),
                    where('messageUTC', '>=', utcStart),
                    where('messageUTC', '<=', utcEnd),
                    orderBy('messageUTC'),
                    limit(batchSize),
                    startAfter(lastDoc)
                );
            } else {
                q = query(
                    collection(db, 'data', props.dataID, 'location'),
                    where('messageUTC', '>=', utcStart),
                    where('messageUTC', '<=', utcEnd),
                    orderBy('messageUTC'),
                    limit(batchSize)
                );
            }
            const querySnapshot = await getDocs(q);
            
            //save the size of the snapshot and the last document retrieved.
            size = querySnapshot.size;
            lastDoc = querySnapshot.docs[size - 1];

            //start storing the coordinates
            querySnapshot.forEach((document) => {
                const data = document.data();
                const newCoord = {lat: data.lat, lng: data.lon};
                if (previousCoord && !isWithinRadius(previousCoord, newCoord, geofenceRadius)) {
                    polylineCoords.push(newCoord);
                    previousCoord = newCoord;
                    bounds.extend(newCoord);
                }
            });

        } while (size >= batchSize);

        polyline = new google.maps.Polyline({
            path: polylineCoords,
            geodesic: true,
            strokeColor: '#FF0000',
            strokeOpacity: 1.0,
            strokeWeight: 2,
        });

        if (map) {
            polyline.setMap(map);
            map.fitBounds(bounds);
        }
        loading.value = false;
        showPolyline.value = true;
    };
</script>

<template>
    <div class="bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-sm overflow-hidden shadow-mc-sm h-full flex flex-col">
        <div class="h-[3px] bg-mc-red shrink-0"></div>
        <!-- Header: title + last update -->
        <div class="px-4 pt-3 pb-2 border-b border-mc-steel dark:border-line-on-dark shrink-0">
            <h1 class="font-display font-extrabold text-[15px] leading-tight text-mc-ink dark:text-on-dark m-0">{{ template.title || 'Map' }}</h1>
            <p class="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-muted dark:text-on-dark-muted mt-1">
                Last Update: {{ lastUpdate }}
            </p>
        </div>
        <!-- Controls toolbar -->
        <div class="flex items-center gap-2 px-4 py-2 border-b border-mc-steel dark:border-line-on-dark shrink-0 flex-wrap">
            <DatePicker v-model="dateValue" class="w-[450px]"></DatePicker>
            <button :disabled="loading" @click="generatePolyline" class="default-button">View Location History</button>
            <button v-if="showPolyline" @click="grabLocation" class="default-button">Back to Current Location</button>
            <LoadingAnimation3 v-if="loading" class="mx-2" size="30px"></LoadingAnimation3>
        </div>
        <!-- Map container -->
        <div class="relative flex-1" ref="mapContainer">
            <div ref="control" class="absolute">
                <button
                    v-if="!showPolyline"
                    class="h-[40px] px-4 font-body font-bold text-sm bg-mc-white dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark text-mc-ink dark:text-on-dark rounded-mc-sm hover:bg-mc-cloud dark:hover:bg-mc-ink transition-colors duration-160 mt-[10px] ml-[10px]"
                    @click="recenterMap"
                >Recenter</button>
            </div>
        </div>
    </div>
</template>
